import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Truck, CheckCircle, Clock, CreditCard, MapPin, ExternalLink, Copy, MessageSquare, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await fetch(`${API_URL}/api/orders/${orderId}/detail`);
        if (res.ok) setData(await res.json());
      } catch (err) {
        console.error('Failed to fetch order:', err);
      } finally {
        setLoading(false);
      }
    };
    if (orderId) fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-stone-50 p-4">
        <p className="text-stone-500 text-center mt-20">Order not found</p>
      </div>
    );
  }

  const { order, product, buyer, seller, timeline } = data;
  const productImg = product?.thumbnail || product?.images?.[0];

  const stepIcons = {
    ordered: Clock,
    paid: CreditCard,
    shipped: Truck,
    delivered: Package,
    completed: CheckCircle,
  };

  const statusColors = {
    pending: 'text-amber-600 bg-amber-50',
    shipped: 'text-blue-600 bg-blue-50',
    delivered: 'text-purple-600 bg-purple-50',
    completed: 'text-emerald-600 bg-emerald-50',
    cancelled: 'text-red-600 bg-red-50',
  };

  const statusLabel = (order.status || 'pending').charAt(0).toUpperCase() + (order.status || 'pending').slice(1);

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="order-tracking-page">
      {/* Header */}
      <div className="bg-emerald-900 text-white px-4 py-5">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate('/orders')} data-testid="tracking-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-display text-lg">Order Tracking</h1>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-emerald-300 text-xs font-mono">#{order.id?.slice(0, 8)}</p>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColors[order.status] || 'text-stone-500 bg-stone-100'}`}>
            {statusLabel}
          </span>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4 max-w-lg mx-auto">
        {/* Product card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-stone-100" data-testid="tracking-product-card">
          <div className="flex gap-3">
            {productImg ? (
              <img src={productImg} alt="" className="w-16 h-16 rounded-xl object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-stone-100 flex items-center justify-center">
                <Package className="w-6 h-6 text-stone-300" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-stone-900 text-sm truncate">{order.product_title || product?.title}</h3>
              <p className="text-xs text-stone-400 mt-0.5">{product?.condition} · {product?.category}</p>
              <p className="text-lg font-bold text-emerald-800 mt-1">${parseFloat(order.total_price || 0).toFixed(2)}</p>
              {order.card_fee > 0 && (
                <p className="text-[10px] text-stone-400 mt-0.5" data-testid="tracking-fee-note">
                  Includes ${parseFloat(order.card_fee).toFixed(2)} card processing fee
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-stone-100" data-testid="order-timeline">
          <h3 className="font-semibold text-stone-900 text-sm mb-5">Order Timeline</h3>
          <div className="relative">
            {timeline.map((step, i) => {
              const Icon = stepIcons[step.step] || Clock;
              const isLast = i === timeline.length - 1;
              return (
                <div key={step.step} className="flex gap-4 relative" data-testid={`timeline-${step.step}`}>
                  {/* Vertical line */}
                  {!isLast && (
                    <div className={`absolute left-[15px] top-9 w-0.5 h-[calc(100%-12px)] ${
                      step.done ? 'bg-emerald-500' : 'bg-stone-200'
                    }`} />
                  )}
                  {/* Icon */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 transition-all ${
                    step.done
                      ? 'bg-emerald-600 text-white'
                      : 'bg-stone-100 text-stone-400'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  {/* Content */}
                  <div className={`pb-6 ${isLast ? 'pb-0' : ''}`}>
                    <p className={`text-sm font-medium ${step.done ? 'text-stone-900' : 'text-stone-400'}`}>
                      {step.label}
                    </p>
                    {step.done && step.date && (
                      <p className="text-[11px] text-stone-400 mt-0.5">
                        {new Date(step.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                    {step.done && step.detail && (
                      <p className="text-xs text-emerald-700 mt-1 font-medium">{step.detail}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tracking info */}
        {order.tracking_number && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-stone-100" data-testid="tracking-info">
            <h3 className="font-semibold text-stone-900 text-sm mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4 text-emerald-700" /> Tracking Information
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between bg-stone-50 rounded-lg p-3">
                <div>
                  <p className="text-[10px] text-stone-400 uppercase">Tracking Number</p>
                  <p className="text-sm font-mono font-medium text-stone-900">{order.tracking_number}</p>
                </div>
                <button
                  className="text-emerald-600 hover:text-emerald-700 p-1"
                  onClick={() => {
                    navigator.clipboard.writeText(order.tracking_number);
                    toast.success('Tracking number copied');
                  }}
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              {order.tracking_url && (
                <Button
                  variant="outline"
                  className="w-full text-sm border-stone-200"
                  onClick={() => window.open(order.tracking_url, '_blank')}
                  data-testid="track-package-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> Track Package
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Shipping address */}
        {order.shipping_info && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-stone-100">
            <h3 className="font-semibold text-stone-900 text-sm mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-700" /> Delivery Address
            </h3>
            <div className="text-sm text-stone-600">
              <p className="font-medium text-stone-900">{order.shipping_info.full_name}</p>
              <p>{order.shipping_info.address}</p>
              <p>{order.shipping_info.city}, {order.shipping_info.state} {order.shipping_info.postcode}</p>
            </div>
          </div>
        )}

        {/* Buyer & Seller */}
        <div className="grid grid-cols-2 gap-3">
          {buyer && (
            <div className="bg-white rounded-2xl p-3 shadow-sm border border-stone-100 text-center">
              <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-2">Buyer</p>
              <div className="w-10 h-10 rounded-full bg-stone-100 mx-auto mb-1 overflow-hidden">
                {buyer.avatar ? <img src={buyer.avatar} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-stone-400 m-auto mt-2.5" />}
              </div>
              <p className="text-xs font-medium text-stone-900">{buyer.name}</p>
            </div>
          )}
          {seller && (
            <div
              className="bg-white rounded-2xl p-3 shadow-sm border border-stone-100 text-center cursor-pointer hover:border-emerald-200 transition-colors"
              onClick={() => navigate(`/seller/${seller.id}`)}
            >
              <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-2">Seller</p>
              <div className="w-10 h-10 rounded-full bg-stone-100 mx-auto mb-1 overflow-hidden">
                {seller.avatar ? <img src={seller.avatar} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-stone-400 m-auto mt-2.5" />}
              </div>
              <p className="text-xs font-medium text-stone-900">{seller.name}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-3 pt-2">
          <Button
            className="w-full bg-emerald-800 hover:bg-emerald-700 h-11 rounded-xl"
            onClick={() => navigate('/messages')}
            data-testid="message-seller-btn"
          >
            <MessageSquare className="w-4 h-4 mr-2" /> Message {seller?.name?.split(' ')[0] || 'Seller'}
          </Button>
          <Button
            variant="outline"
            className="w-full h-11 rounded-xl border-stone-200"
            onClick={() => navigate('/orders')}
          >
            Back to Orders
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingPage;
